/**
 * itemDescription — composes the read-only "Item Description" line shown
 * under the Item Name across the app (ItemDetail subtitle, Collection
 * table rows, etc.).
 *
 * CDI industry-standard naming order — kept in lock-step with
 * /lib/composeItemName.js so both surfaces agree:
 *
 *   Wine    → Producer · Region · Grape(s) · Vintage
 *             (LOCKED preset — Region is OPTIONAL, auto-skipped when
 *              blank. Bottle Size deliberately excluded from identity
 *              dots per global wine-industry conventions.)
 *
 *   Whisky  → Distillery · Whisky Type · Age · Cask Type · Region · ABV
 *             (LOCKED preset — no legacy fallbacks, no Bottle Size in
 *              the identity dots. ABV suffix rule: bare "40" → "40% ABV";
 *              value with "%" already → "<value> ABV".)
 *
 *   Spirits → Brand · Spirit Type · Age · Cask Type / Finish · Region · ABV
 *             (LOCKED preset — Age and Region are OPTIONAL, auto-skipped
 *              when blank. Bottle Size deliberately excluded from identity
 *              dots per global spirits-industry conventions — Whiskybase,
 *              RumX, Distiller.com, Master of Malt et al.)
 *
 *   Anything else (or no relevant data at all)
 *     `Item details` — preserves the legacy static string.
 *
 * Pure function. No React, no DOM. Takes the raw item + the parent
 * collection (which may be null on initial render) and returns the
 * already-trimmed description string.
 */

function isWhiskyCollection(collection) {
  if (!collection) return false;
  if (collection.type_id === "whisky") return true;
  if (!collection.type_id && /whisk(e)?y/i.test(collection.name || "")) return true;
  return false;
}

// Spirits detection mirrors ItemEntry.isSpiritsCollection — matches
// rum / tequila / cognac / gin / vodka / mezcal / armagnac / brandy /
// liqueur / generic "spirits" collections. Whisky is deliberately
// excluded — it has its own dedicated identity format above.
function isSpiritsCollection(collection) {
  if (!collection) return false;
  if (collection.type_id === "spirits") return true;
  if (collection.type_id === "whisky") return false;
  const n = collection.name || "";
  if (/whisk(e)?y/i.test(n)) return false;
  return /(rum|tequila|cognac|gin|vodka|mezcal|armagnac|brandy|liqueur|spirits?)/i.test(n);
}

// Shared ABV formatter — used by both Whisky and Spirits identity blocks.
// Rules: empty stays empty; existing "%" gets " ABV" suffix; bare numeric
// gets "% ABV" suffix. So "40" / 40 / "40%" all render as "40% ABV".
function formatAbv(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  return s.includes("%") ? `${s} ABV` : `${s}% ABV`;
}

// Detects Wine collections. Loose regex fallback for older items where
// collection.type_id was never persisted.
function isWineCollection(collection) {
  if (!collection) return false;
  if (collection.type_id === "wine") return true;
  if (!collection.type_id && /wine/i.test(collection.name || "")) return true;
  return false;
}

// Compose Item Name from the CURRENT identity fields using the CDI
// industry-standard naming order (kept in lock-step with
// /lib/composeItemName.js so both surfaces agree):
//   Wine    → Producer + Appellation (optional) + Grape + Vintage + Bottle Size
//   Whisky  → Distillery + Whisky Type + Age + Region + ABV
//   Spirits → Producer + Spirit Type + Expression + Region + ABV
// Optional/blank fields are auto-skipped via filter(Boolean). Legacy
// pre-rename field names are preserved as fallbacks. Returns "" when
// the collection isn't a naming collection.
export function composeItemName(item, collection) {
  if (!item) return "";
  const data = item.item_data || {};
  const join = (parts) => parts.filter(Boolean).map((s) => String(s).trim()).filter(Boolean).join(" ").replace(/\s+/g, " ").trim();

  if (isWineCollection(collection)) {
    // Wine Naming (Phase 7, 2026-02):
    //   Producer + [Appellation OR Region fallback] + Grape(s)
    //   + Vintage + ABV + Bottle Size
    // Appellation takes precedence — Region is used only when
    // Appellation is blank. ABV renders as "<num>%" so this composer
    // matches composeAutoName.wine and the description identity block
    // (2026-02 Bottle-Size consistency fix). Blank fields skip via
    // filter(Boolean).
    const appellation = String(data["Appellation"] || "").trim();
    const region = String(data["Region"] || "").trim();
    const wineOrigin = appellation || region;
    const rawAbv = String(data["ABV"] ?? "").trim();
    const abvPct = rawAbv ? (rawAbv.includes("%") ? rawAbv : `${rawAbv}%`) : "";
    return join([
      data["Producer"],
      wineOrigin,
      data["Grape(s)"],
      data["Vintage"],
      abvPct,
      data["Bottle Size"],
    ]);
  }
  if (isWhiskyCollection(collection)) {
    // Whisky Naming (Phase 5, 2026-02):
    //   Distillery + Whisky Type + Age / Classification + ABV + Bottle Size
    // ABV renders as "<num>%" (no "ABV" suffix). Legacy Age / Cask
    // Type / Region rows stay as metadata only.
    const rawAbv = String(data["ABV"] ?? "").trim();
    const abvPct = rawAbv ? (rawAbv.includes("%") ? rawAbv : `${rawAbv}%`) : "";
    return join([
      data["Distillery"],
      data["Whisky Type"],
      data["Age / Classification"],
      abvPct,
      data["Bottle Size"],
    ]);
  }
  if (isSpiritsCollection(collection)) {
    // Spirits Naming (Phase 2, 2026-02):
    //   Brand + Spirit Type + Age / Classification + ABV + Bottle Size
    // ABV renders as "<num>%" (no "ABV" suffix). Legacy Age /
    // Cask Type / Finish / Region live on as metadata rows only.
    const rawAbv = String(data["ABV"] ?? "").trim();
    const abvPct = rawAbv ? (rawAbv.includes("%") ? rawAbv : `${rawAbv}%`) : "";
    return join([
      data["Brand"],
      data["Spirit Type"],
      data["Age Classification"],
      abvPct,
      data["Bottle Size"],
    ]);
  }
  return "";
}

export function composeItemDescription(item, collection) {
  if (!item) return "Item details";
  const data = item.item_data || {};
  const displayTitle = data["Item Name"] || item.name || item.item_name || "Untitled";
  const bottleSize = String(data["Bottle Size"] || "").trim();

  // Whisky identity block (Phase 5, 2026-02):
  //   "<Distillery> · <Whisky Type> · <Age / Classification> · <ABV> · <Bottle Size>"
  // ABV renders as "<num>%" only (NO " ABV" suffix). Blank fields
  // auto-skip via filter(Boolean). Legacy Age / Cask Type / Region
  // rows stay on the schema but are metadata-only — they no longer
  // appear on the identity line.
  if (isWhiskyCollection(collection)) {
    const distillery = String(data["Distillery"] || "").trim();
    const whiskyType = String(data["Whisky Type"] || "").trim();
    const ageClass = String(data["Age / Classification"] || "").trim();
    const rawAbv = String(data["ABV"] ?? "").trim();
    const abv = rawAbv ? (rawAbv.includes("%") ? rawAbv : `${rawAbv}%`) : "";
    const bottleSize2 = String(data["Bottle Size"] || "").trim();
    const blocks = [distillery, whiskyType, ageClass, abv, bottleSize2].filter(Boolean);
    return blocks.length ? blocks.join(" · ") : "Item details";
  }

  // Spirits identity block (Phase 2, 2026-02):
  //   "<Brand> · <Spirit Type> · <Age / Classification> · <ABV> · <Bottle Size>"
  // ABV renders as "<num>%" only (NO " ABV" suffix). Blank fields
  // auto-skip via filter(Boolean). Legacy Age / Cask Type / Finish /
  // Region rows stay on the schema but are metadata-only — they no
  // longer appear on the identity line.
  if (isSpiritsCollection(collection)) {
    const brand = String(data["Brand"] || "").trim();
    const spiritType = String(data["Spirit Type"] || "").trim();
    const ageClass = String(data["Age Classification"] || "").trim();
    const rawAbv = String(data["ABV"] ?? "").trim();
    const abv = rawAbv ? (rawAbv.includes("%") ? rawAbv : `${rawAbv}%`) : "";
    const bottleSize2 = String(data["Bottle Size"] || "").trim();
    const blocks = [brand, spiritType, ageClass, abv, bottleSize2].filter(Boolean);
    return blocks.length ? blocks.join(" · ") : "Item details";
  }

  // Wine identity block (Phase 7, 2026-02):
  //   "<Producer> · <Appellation OR Region> · <Grape(s)> · <Vintage> · <ABV> · <Bottle Size>"
  // Appellation takes precedence; Region is used only when
  // Appellation is blank. ABV renders as "<num>%" (no "ABV" suffix).
  // Bottle Size is the trailing token per the CDI global-industry
  // naming spec. Blank fields skip via filter(Boolean).
  if (isWineCollection(collection)) {
    const producer = String(data["Producer"] || "").trim();
    const appellation = String(data["Appellation"] || "").trim();
    const region = String(data["Region"] || "").trim();
    const wineOrigin = appellation || region;
    const grape = String(data["Grape(s)"] || "").trim();
    const vintage = String(data["Vintage"] || "").trim();
    const rawAbv = String(data["ABV"] ?? "").trim();
    const abv = rawAbv ? (rawAbv.includes("%") ? rawAbv : `${rawAbv}%`) : "";
    const bottleSize2 = String(data["Bottle Size"] || "").trim();
    const blocks = [producer, wineOrigin, grape, vintage, abv, bottleSize2].filter(Boolean);
    return blocks.length ? blocks.join(" · ") : "Item details";
  }

  return bottleSize ? `${displayTitle} ${bottleSize}` : "Item details";
}
